/**
 * OMNI Decision Inbox — one consolidated queue of everything waiting on a
 * human, so nobody has to search five modules to find their decisions.
 *
 * Categories, in operational priority order:
 *   DECISION REQUIRED   — EITL award proposals awaiting a decision
 *   RECOVERY OPTION     — schedule recovery proposals awaiting a decision
 *   APPROVAL REQUIRED   — generic approvals + operator actions held for
 *                         confirmation
 *   BLOCKER             — blocked work from the risk queue
 *   INFORMATION REQUIRED— document reviews and unknowns needing input
 *   RISK                — deadlines and long-lead exposure
 *
 * Every card deep-links into the record where the decision is actually made.
 * Sources that are feature-disabled say so; they are never silently zero.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "../services/api";
import {
  awardWorkflowApi,
  schedulingAgentApi,
  classifyWorkflowError,
} from "../services/intelligenceService";
import exchangeApi from "../services/exchangeService";
import { useAuth } from "../App";
import { canEditSchedule } from "../lib/roles";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  SectionLoading,
  SectionEmpty,
  PartialDataNotice,
} from "../components/workflow/WorkflowStates";
import { fmtMoney } from "../features/bid-packages/levelingUtils";
import {
  Landmark,
  LifeBuoy,
  FileCheck,
  OctagonAlert,
  HelpCircle,
  AlertTriangle,
  ChevronRight,
  Inbox,
  CheckCircle2,
  XCircle,
  Loader2,
  Bot,
  IdCard,
} from "lucide-react";

const CATEGORY_ORDER = [
  {
    key: "decision",
    label: "DECISION REQUIRED",
    Icon: Landmark,
    className: "text-amber-400",
    border: "border-amber-500/30",
  },
  {
    key: "recovery",
    label: "RECOVERY OPTION",
    Icon: LifeBuoy,
    className: "text-violet-400",
    border: "border-violet-500/30",
  },
  {
    key: "approval",
    label: "APPROVAL REQUIRED",
    Icon: FileCheck,
    className: "text-blue-400",
    border: "border-blue-500/30",
  },
  {
    key: "blocker",
    label: "BLOCKER",
    Icon: OctagonAlert,
    className: "text-red-400",
    border: "border-red-500/30",
  },
  {
    key: "identity",
    label: "IDENTITY MATCH REVIEW",
    Icon: IdCard,
    className: "text-teal-400",
    border: "border-teal-500/30",
  },
  {
    key: "information",
    label: "INFORMATION REQUIRED",
    Icon: HelpCircle,
    className: "text-cyan-400",
    border: "border-cyan-500/30",
  },
  {
    key: "risk",
    label: "RISK",
    Icon: AlertTriangle,
    className: "text-orange-400",
    border: "border-orange-500/30",
  },
];

const PACKAGE_SCAN_CAP = 8;
const PROJECT_SCAN_CAP = 8;

const ApprovalsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [truncationNote, setTruncationNote] = useState(null);

  const [legacyDecision, setLegacyDecision] = useState(null); // {approval, status}
  const [legacyNote, setLegacyNote] = useState("");
  const [acting, setActing] = useState(false);

  /**
   * A note belongs to ONE approval. The dialog is a single shared instance,
   * so without clearing on dismissal a rejection reason typed for approval A
   * and then abandoned becomes approval B's audited decision note — and it
   * also pre-satisfies the "a rejection needs a reason" guard with a reason
   * that was never about B.
   */
  const closeLegacyDecision = () => {
    setLegacyDecision(null);
    setLegacyNote("");
  };

  const load = useCallback(async () => {
    setLoading(true);
    const collected = [];
    const missing = [];
    let truncated = [];

    const [approvalsRes, projectsRes, bidsRes, riskRes, actionsRes] = await Promise.allSettled([
      // GET /approvals takes no filters — it returns the whole tenant list.
      apiClient.get("/approvals"),
      apiClient.get("/projects"),
      apiClient.get("/bids"),
      apiClient.get("/dashboard/risk-queue"),
      apiClient.get("/operator/actions", { params: { status: "needs_confirmation" } }),
    ]);

    // Legacy approvals (pending only) → APPROVAL REQUIRED
    if (approvalsRes.status === "fulfilled") {
      (approvalsRes.value.data || []).filter((a) => a.status === "pending").forEach((a) => {
        collected.push({
          id: `approval-${a.id}`,
          category: "approval",
          title: a.title || a.approval_type || "Approval request",
          lines: [a.description].filter(Boolean),
          meta: a.approval_type,
          action: { kind: "legacy-approval", approval: a },
        });
      });
    } else {
      missing.push({ name: "Approvals", reason: "failed" });
    }

    // Operator actions held for confirmation → APPROVAL REQUIRED
    if (actionsRes.status === "fulfilled") {
      (actionsRes.value.data || []).forEach((action) => {
        collected.push({
          id: `operator-${action.id}`,
          category: "approval",
          title: `Operator action: ${action.tool_name || action.action_type}`,
          lines: ["The assistant is holding this until you confirm or deny it."],
          meta: "operator",
          Icon: Bot,
          action: { kind: "operator-action", action },
        });
      });
    }

    // EITL award proposals → DECISION REQUIRED
    const bids = bidsRes.status === "fulfilled" ? bidsRes.value.data || [] : [];
    if (bidsRes.status === "rejected") missing.push({ name: "Bid packages", reason: "failed" });
    const openPackages = bids.filter((b) => ["upcoming", "pending", "submitted"].includes(b.status));
    if (openPackages.length > PACKAGE_SCAN_CAP) {
      truncated.push(
        `award and shortlist decisions scanned the ${PACKAGE_SCAN_CAP} most recent open packages of ${openPackages.length}`
      );
    }
    let awardDisabled = false;
    for (const pkg of openPackages.slice(0, PACKAGE_SCAN_CAP)) {
      try {
        const proposals = await awardWorkflowApi.listProposals(pkg.id);
        proposals
          .filter((p) => p.state === "PROPOSED")
          .forEach((p) => {
            collected.push({
              id: `award-${p.proposalId}`,
              category: "decision",
              title: `${pkg.title || pkg.trade || "Package"} award`,
              lines: [
                p.recommendedBidderName
                  ? `${p.recommendedBidderName} recommended${
                      p.evaluatedAmount ? ` · ${fmtMoney(p.evaluatedAmount)} evaluated` : ""
                    }`
                  : "A recommendation is waiting for your decision.",
                p.confidence ? `Confidence: ${p.confidence}` : null,
              ].filter(Boolean),
              meta: pkg.project_name,
              cta: {
                label: "Review Decision",
                onClick: () => navigate(`/procurement/packages/${pkg.id}`),
              },
            });
          });
      } catch (err) {
        const c = classifyWorkflowError(err);
        if (["disabled", "not-entitled", "no-access"].includes(c.kind)) {
          awardDisabled = c.kind;
          break;
        }
      }
    }
    if (awardDisabled) missing.push({ name: "Award workflow", reason: awardDisabled });

    /**
     * Shortlist entries awaiting a human decision → DECISION REQUIRED.
     *
     * The backend states this category itself: automation writes PROPOSED and
     * nothing else, so a PROPOSED entry is by definition waiting on a person.
     * It rides the same bounded package scan as awards, and its truncation is
     * disclosed the same way.
     */
    let shortlistDisabled = false;
    for (const pkg of openPackages.slice(0, PACKAGE_SCAN_CAP)) {
      if (!pkg.project_id) continue;
      try {
        const list = await exchangeApi.getShortlist(pkg.project_id, pkg.id);
        const awaiting = list?.stateFacets?.PROPOSED || 0;
        if (awaiting > 0) {
          collected.push({
            id: `shortlist-${pkg.id}`,
            category: "decision",
            title: `${pkg.title || pkg.trade || "Package"} shortlist`,
            lines: [
              `${awaiting} proposed candidate${awaiting === 1 ? "" : "s"} waiting to be shortlisted or excluded.`,
              "A proposed candidate is on nobody's shortlist until a person decides.",
            ],
            meta: pkg.project_name,
            cta: {
              label: "Review Candidates",
              onClick: () =>
                navigate(
                  `/exchange/build?project_id=${pkg.project_id}&package_id=${pkg.id}&tab=shortlist`
                ),
            },
          });
        }
      } catch (err) {
        const c = classifyWorkflowError(err);
        if (["disabled", "not-entitled", "no-access"].includes(c.kind)) {
          shortlistDisabled = c.kind;
          break;
        }
      }
    }
    if (shortlistDisabled) missing.push({ name: "Provider shortlists", reason: shortlistDisabled });

    // Recovery proposals → RECOVERY OPTION
    const projects = projectsRes.status === "fulfilled" ? projectsRes.value.data || [] : [];
    if (projectsRes.status === "rejected") missing.push({ name: "Projects", reason: "failed" });
    if (projects.length > PROJECT_SCAN_CAP) {
      truncated.push(
        `recovery decisions scanned the ${PROJECT_SCAN_CAP} most recent projects of ${projects.length}`
      );
    }
    let saDisabled = false;
    for (const project of projects.slice(0, PROJECT_SCAN_CAP)) {
      try {
        const proposals = await schedulingAgentApi.listProposals(project.id);
        proposals
          .filter((p) => p.state === "PROPOSED")
          .forEach((p) => {
            collected.push({
              id: `recovery-${p.proposalId}`,
              category: "recovery",
              title: `${project.name}: ${(p.proposalKind || "recovery").toLowerCase()} proposal`,
              lines: [
                `Trigger: ${p.trigger || "schedule slip"} · confidence ${p.confidence || "UNKNOWN"}`,
              ],
              meta: project.name,
              cta: {
                label: "Review Recovery",
                onClick: () => navigate(`/control-tower?project_id=${project.id}`),
              },
            });
          });
      } catch (err) {
        const c = classifyWorkflowError(err);
        if (["disabled", "not-entitled", "no-access"].includes(c.kind)) {
          saDisabled = c.kind;
          break;
        }
      }
    }
    if (saDisabled) missing.push({ name: "Scheduling agent", reason: saDisabled });

    // Unconfirmed identity links → IDENTITY MATCH REVIEW.
    // An unconfirmed link contributes no facts to any passport or match, so
    // leaving one undecided quietly degrades every sourcing answer.
    try {
      const providers = await exchangeApi.listProviders();
      const facets = providers?.stateFacets || {};
      const awaiting =
        (facets.PROPOSED_MATCH || 0) + (facets.DISPUTED || 0) + (facets.UNRESOLVED || 0);
      if (awaiting > 0) {
        collected.push({
          id: "identity-links",
          category: "identity",
          title: `${awaiting} provider identity link${awaiting === 1 ? "" : "s"} need a decision`,
          lines: [
            "Until a link is confirmed it contributes no facts to a provider passport or a match.",
            [
              facets.PROPOSED_MATCH ? `${facets.PROPOSED_MATCH} proposed` : null,
              facets.DISPUTED ? `${facets.DISPUTED} disputed` : null,
              facets.UNRESOLVED ? `${facets.UNRESOLVED} unresolved` : null,
            ]
              .filter(Boolean)
              .join(" · "),
          ].filter(Boolean),
          cta: { label: "Review providers", onClick: () => navigate("/exchange/providers") },
        });
      }
    } catch (err) {
      const c = classifyWorkflowError(err);
      if (["disabled", "not-entitled", "no-access"].includes(c.kind)) {
        missing.push({ name: "Exchange identity", reason: c.kind });
      }
    }

    // Risk queue → BLOCKER / INFORMATION / RISK
    if (riskRes.status === "fulfilled") {
      (riskRes.value.data?.items || []).forEach((item, idx) => {
        if (item.type === "approval") return; // already covered above
        const category =
          item.type === "blocked_activity"
            ? "blocker"
            : item.type === "document_review"
              ? "information"
              : "risk";
        const target =
          item.type === "blocked_activity"
            ? () => navigate(`/projects/${item.project_id}`)
            : item.type === "document_review"
              ? () => navigate(`/projects/${item.project_id}/documents`)
              : () => navigate(`/procurement${item.project_id ? `?project_id=${item.project_id}` : ""}`);
        collected.push({
          id: `risk-${item.type}-${item.id || idx}`,
          category,
          title: item.title,
          lines: [
            item.project_name ? `Project: ${item.project_name}` : null,
            item.due_date ? `Due ${new Date(item.due_date).toLocaleDateString()}` : null,
          ].filter(Boolean),
          meta: item.severity,
          severity: item.severity,
          cta: { label: "Open", onClick: target },
        });
      });
    } else {
      missing.push({ name: "Risk queue", reason: "failed" });
    }

    setItems(collected);
    setUnavailable(missing);
    setTruncationNote(truncated.length ? `Coverage note: ${truncated.join("; ")}.` : null);
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const decideLegacy = async () => {
    if (!legacyDecision) return;
    if (legacyDecision.status === "rejected" && legacyNote.trim().length === 0) {
      toast.error("Give a reason for the rejection — it becomes the record.");
      return;
    }
    setActing(true);
    try {
      await apiClient.put(`/approvals/${legacyDecision.approval.id}`, {
        status: legacyDecision.status,
        decision_notes: legacyNote.trim() || undefined,
      });
      toast.success(`Approval ${legacyDecision.status}.`);
      setLegacyDecision(null);
      setLegacyNote("");
      load();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setActing(false);
    }
  };

  const actOnOperatorAction = async (action, verb) => {
    setActing(true);
    try {
      await apiClient.post(`/operator/actions/${action.id}/${verb}`);
      toast.success(verb === "approve" ? "Action confirmed and executed." : "Action denied.");
      load();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setActing(false);
    }
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(CATEGORY_ORDER.map((c) => [c.key, []]));
    const severityRank = { high: 0, medium: 1, low: 2 };
    items.forEach((i) => (g[i.category] || g.risk).push(i));
    Object.values(g).forEach((list) =>
      list.sort((a, b) => (severityRank[a.severity] ?? 3) - (severityRank[b.severity] ?? 3))
    );
    return g;
  }, [items]);

  const total = items.length;
  // Legacy approvals admit superintendent as well (backend PUT /approvals/{id}).
  const userCanDecide = canEditSchedule(user);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Decision Inbox</h1>
          <p className="text-muted-foreground mt-1">
            Everything waiting on a human, in one queue — award decisions, recovery options,
            approvals, blockers, and risks, each linking to the record where you act.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_ORDER.map((c) => {
            const count = grouped[c.key]?.length || 0;
            if (!count) return null;
            return (
              <Badge key={c.key} variant="outline" className={`bg-muted/40 border-border font-normal ${c.className}`}>
                <c.Icon className="w-3 h-3 mr-1" />
                {c.label}: {count}
              </Badge>
            );
          })}
        </div>
      </div>

      <PartialDataNotice unavailable={unavailable} />
      {truncationNote && <p className="text-xs text-muted-foreground">{truncationNote}</p>}

      {loading ? (
        <SectionLoading label="Collecting everything that needs you…" />
      ) : total === 0 ? (
        <SectionEmpty
          icon={Inbox}
          title="Nothing is waiting on you"
          hint="New award decisions, recovery proposals, approvals, and blockers land here as the system finds them."
        />
      ) : (
        CATEGORY_ORDER.map((cat) => {
          const list = grouped[cat.key];
          if (!list || list.length === 0) return null;
          return (
            <div key={cat.key} className="space-y-2">
              <h2 className={`text-sm font-semibold flex items-center gap-1.5 ${cat.className}`}>
                <cat.Icon className="w-4 h-4" />
                {cat.label}
                <span className="text-muted-foreground font-normal">({list.length})</span>
              </h2>
              <div className="space-y-2">
                {list.map((item) => (
                  <Card key={item.id} className={`${cat.border} bg-card`}>
                    <CardContent className="py-3 px-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-sm font-medium flex items-center gap-1.5">
                          {item.Icon && <item.Icon className="w-3.5 h-3.5 text-muted-foreground" />}
                          {item.title}
                        </p>
                        {item.lines.map((line, i) => (
                          <p key={i} className="text-xs text-muted-foreground">
                            {line}
                          </p>
                        ))}
                        {item.meta && (
                          <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wide">
                            {item.meta}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {item.cta && (
                          <Button variant="outline" size="sm" onClick={item.cta.onClick}>
                            {item.cta.label}
                            <ChevronRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        )}
                        {item.action?.kind === "legacy-approval" &&
                          (userCanDecide ? (
                            <>
                              <Button
                                size="sm"
                                disabled={acting}
                                onClick={() =>
                                  setLegacyDecision({ approval: item.action.approval, status: "approved" })
                                }
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={acting}
                                onClick={() =>
                                  setLegacyDecision({ approval: item.action.approval, status: "rejected" })
                                }
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">Decision reserved for PM/superintendent/admin</span>
                          ))}
                        {item.action?.kind === "operator-action" && (
                          <>
                            <Button size="sm" disabled={acting} onClick={() => actOnOperatorAction(item.action.action, "approve")}>
                              Confirm
                            </Button>
                            <Button size="sm" variant="outline" disabled={acting} onClick={() => actOnOperatorAction(item.action.action, "deny")}>
                              Deny
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Legacy approval decision dialog */}
      <Dialog open={!!legacyDecision} onOpenChange={(open) => !open && closeLegacyDecision()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {legacyDecision?.status === "approved" ? "Approve" : "Reject"}:{" "}
              {legacyDecision?.approval?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              rows={3}
              value={legacyNote}
              onChange={(e) => setLegacyNote(e.target.value)}
              placeholder={
                legacyDecision?.status === "approved"
                  ? "Optional note for the record"
                  : "Why is this rejected? (required)"
              }
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={closeLegacyDecision}>
                Cancel
              </Button>
              <Button size="sm" onClick={decideLegacy} disabled={acting}>
                {acting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
